import { MOCK_STUDENT_IDS, MOCK_STUDENTS_MAP } from './mockStudents';

// --- Types (matching the hook types) ---

type FlaggedMessageCategory = {
  category: string;
  score: number;
};

type FlaggedMessage = {
  annotationId: string;
  annotationNotes: string | null;
  annotationReviewedAt: string | null;
  annotationReviewedById: string | null;
  annotationStatus: 'PENDING' | 'REVIEWED' | 'DISMISSED';
  categories: FlaggedMessageCategory[];
  content: string;
  conversationId: string;
  createdAt: string;
  groupName?: string;
  messageId: string;
  reviewerName?: string;
  studentExternalId: string;
  studentLastName: string;
  studentName: string;
};

type TopicItem = {
  disciplineId: string;
  disciplineName: string;
  frequency: number;
  topic: string;
  topicId: string | null;
};

type TopicDetailUser = {
  externalId: string;
  extendedSearchCount: number;
  frequency: number;
  lastName: string;
  name: string;
  userId: string;
};

// --- Constants ---

export const MOCK_TOTAL_FLAGGED_MESSAGES = 6;

const DISCIPLINE_MATH_ID = 'mock-discipline-math';
const DISCIPLINE_LANG_ID = 'mock-discipline-lang';

// --- Flagged messages (ES) ---

const MOCK_FLAGGED_MESSAGES_ES: FlaggedMessage[] = [
  {
    annotationId: 'mock-annotation-fm-01',
    annotationNotes: null,
    annotationReviewedAt: null,
    annotationReviewedById: null,
    annotationStatus: 'PENDING',
    categories: [{ category: 'harassment', score: 0.92 }],
    content: 'El profesor no tiene ni idea, siempre nos pone ejercicios estupidos y nos trata fatal.',
    conversationId: 'mock-conv-01',
    createdAt: '2026-04-06T09:15:00Z',
    groupName: '5\u00ba A',
    messageId: 'mock-msg-01',
    studentExternalId: MOCK_STUDENT_IDS[0],
    studentLastName: 'Garcia',
    studentName: 'Maria',
  },
  {
    annotationId: 'mock-annotation-fm-03',
    annotationNotes: null,
    annotationReviewedAt: null,
    annotationReviewedById: null,
    annotationStatus: 'PENDING',
    categories: [
      { category: 'sexual', score: 0.78 },
      { category: 'harassment', score: 0.45 },
    ],
    content: 'Dame el numero de telefono de mi profesora, necesito hablar con ella por WhatsApp.',
    conversationId: 'mock-conv-03',
    createdAt: '2026-04-07T08:45:00Z',
    groupName: '5\u00ba A',
    messageId: 'mock-msg-03',
    studentExternalId: MOCK_STUDENT_IDS[2],
    studentLastName: 'Martinez',
    studentName: 'Ana',
  },
  {
    annotationId: 'mock-annotation-fm-05',
    annotationNotes: null,
    annotationReviewedAt: null,
    annotationReviewedById: null,
    annotationStatus: 'PENDING',
    categories: [{ category: 'harassment/threatening', score: 0.95 }],
    content: 'Mi companero Pablo es un tonto, dile que no sabe hacer nada bien.',
    conversationId: 'mock-conv-05',
    createdAt: '2026-04-07T14:10:00Z',
    groupName: '5\u00ba B',
    messageId: 'mock-msg-05',
    studentExternalId: MOCK_STUDENT_IDS[4],
    studentLastName: 'Hernandez',
    studentName: 'Sofia',
  },
];

// --- Portuguese overrides for flagged messages ---

const PT_FLAGGED_OVERRIDES: Record<string, { content: string }> = {
  'mock-annotation-fm-01': {
    content: 'O professor nao sabe nada, sempre nos da exercicios estupidos e nos trata mal.',
  },
  'mock-annotation-fm-03': {
    content: 'Me da o numero de telefone da minha professora, preciso falar com ela pelo WhatsApp.',
  },
  'mock-annotation-fm-05': {
    content: 'Meu colega Pablo e um idiota, diz pra ele que nao sabe fazer nada direito.',
  },
};

// --- Topic ranking (ES) ---

const MOCK_TOPIC_RANKING_ES: TopicItem[] = [
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 27,
    topic: 'Ecuaciones de primer grado',
    topicId: 'mock-topic-01',
  },
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 15,
    topic: 'Area de figuras geometricas',
    topicId: 'mock-topic-02',
  },
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 14,
    topic: 'Media aritmetica y graficos de barras',
    topicId: 'mock-topic-03',
  },
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 13,
    topic: 'Fracciones equivalentes',
    topicId: 'mock-topic-04',
  },
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 11,
    topic: 'Operaciones con numeros enteros',
    topicId: 'mock-topic-05',
  },
  {
    disciplineId: DISCIPLINE_MATH_ID,
    disciplineName: 'Matematicas',
    frequency: 11,
    topic: 'Potenciacion y raices cuadradas',
    topicId: 'mock-topic-06',
  },
  {
    disciplineId: DISCIPLINE_LANG_ID,
    disciplineName: 'Lengua',
    frequency: 10,
    topic: 'Sujeto y predicado en oraciones',
    topicId: 'mock-topic-07',
  },
  {
    disciplineId: DISCIPLINE_LANG_ID,
    disciplineName: 'Lengua',
    frequency: 10,
    topic: 'Reglas de acentuacion',
    topicId: 'mock-topic-08',
  },
  {
    disciplineId: DISCIPLINE_LANG_ID,
    disciplineName: 'Lengua',
    frequency: 10,
    topic: 'Verbos irregulares',
    topicId: 'mock-topic-09',
  },
  {
    disciplineId: DISCIPLINE_LANG_ID,
    disciplineName: 'Lengua',
    frequency: 8,
    topic: 'Analisis sintactico',
    topicId: 'mock-topic-10',
  },
];

// --- Portuguese overrides for topics ---

type TopicTextOverride = {
  disciplineName: string;
  topic: string;
};

const PT_TOPIC_OVERRIDES: Record<string, TopicTextOverride> = {
  'mock-topic-01': { disciplineName: 'Matematica', topic: 'Equacoes do primeiro grau' },
  'mock-topic-02': { disciplineName: 'Matematica', topic: 'Area de figuras geometricas' },
  'mock-topic-03': { disciplineName: 'Matematica', topic: 'Media aritmetica e graficos de barras' },
  'mock-topic-04': { disciplineName: 'Matematica', topic: 'Fracoes equivalentes' },
  'mock-topic-05': { disciplineName: 'Matematica', topic: 'Operacoes com numeros inteiros' },
  'mock-topic-06': { disciplineName: 'Matematica', topic: 'Potenciacao e raizes quadradas' },
  'mock-topic-07': { disciplineName: 'Lingua Portuguesa', topic: 'Sujeito e predicado em oracoes' },
  'mock-topic-08': { disciplineName: 'Lingua Portuguesa', topic: 'Regras de acentuacao' },
  'mock-topic-09': { disciplineName: 'Lingua Portuguesa', topic: 'Verbos irregulares' },
  'mock-topic-10': { disciplineName: 'Lingua Portuguesa', topic: 'Analise sintatica' },
};

// --- Topic detail users (ES) — keyed by topicId ---

function getStudentInfo(index: number): { externalId: string; lastName: string; name: string; userId: string } {
  const id = MOCK_STUDENT_IDS[index];
  const info = MOCK_STUDENTS_MAP.get(id);
  return {
    externalId: id,
    lastName: info?.lastName ?? '',
    name: info?.name ?? '',
    userId: id,
  };
}

const MOCK_TOPIC_DETAIL_ES: Record<string, TopicDetailUser[]> = {
  'mock-topic-01': [
    { ...getStudentInfo(0), extendedSearchCount: 3, frequency: 9 },
    { ...getStudentInfo(3), extendedSearchCount: 1, frequency: 7 },
    { ...getStudentInfo(6), extendedSearchCount: 2, frequency: 6 },
    { ...getStudentInfo(8), extendedSearchCount: 0, frequency: 5 },
  ],
  'mock-topic-02': [
    { ...getStudentInfo(2), extendedSearchCount: 2, frequency: 6 },
    { ...getStudentInfo(5), extendedSearchCount: 1, frequency: 5 },
    { ...getStudentInfo(9), extendedSearchCount: 0, frequency: 4 },
  ],
  'mock-topic-03': [
    { ...getStudentInfo(1), extendedSearchCount: 1, frequency: 5 },
    { ...getStudentInfo(4), extendedSearchCount: 2, frequency: 5 },
    { ...getStudentInfo(7), extendedSearchCount: 0, frequency: 4 },
  ],
  'mock-topic-04': [
    { ...getStudentInfo(0), extendedSearchCount: 0, frequency: 5 },
    { ...getStudentInfo(3), extendedSearchCount: 1, frequency: 4 },
    { ...getStudentInfo(10), extendedSearchCount: 0, frequency: 4 },
  ],
  'mock-topic-05': [
    { ...getStudentInfo(6), extendedSearchCount: 1, frequency: 4 },
    { ...getStudentInfo(8), extendedSearchCount: 0, frequency: 4 },
    { ...getStudentInfo(11), extendedSearchCount: 1, frequency: 3 },
  ],
  'mock-topic-06': [
    { ...getStudentInfo(2), extendedSearchCount: 2, frequency: 4 },
    { ...getStudentInfo(5), extendedSearchCount: 0, frequency: 4 },
    { ...getStudentInfo(9), extendedSearchCount: 1, frequency: 3 },
  ],
  'mock-topic-07': [
    { ...getStudentInfo(1), extendedSearchCount: 0, frequency: 4 },
    { ...getStudentInfo(7), extendedSearchCount: 1, frequency: 3 },
    { ...getStudentInfo(12), extendedSearchCount: 0, frequency: 3 },
  ],
  'mock-topic-08': [
    { ...getStudentInfo(4), extendedSearchCount: 1, frequency: 4 },
    { ...getStudentInfo(10), extendedSearchCount: 0, frequency: 3 },
    { ...getStudentInfo(13), extendedSearchCount: 1, frequency: 3 },
  ],
  'mock-topic-09': [
    { ...getStudentInfo(0), extendedSearchCount: 0, frequency: 4 },
    { ...getStudentInfo(3), extendedSearchCount: 1, frequency: 3 },
    { ...getStudentInfo(11), extendedSearchCount: 0, frequency: 3 },
  ],
  'mock-topic-10': [
    { ...getStudentInfo(1), extendedSearchCount: 1, frequency: 3 },
    { ...getStudentInfo(6), extendedSearchCount: 0, frequency: 3 },
    { ...getStudentInfo(12), extendedSearchCount: 0, frequency: 2 },
  ],
};

// --- Language-aware export functions ---

function isPortuguese(lang: string): boolean {
  return lang.startsWith('pt');
}

export function getMockFlaggedMessages(lang: string): FlaggedMessage[] {
  if (!isPortuguese(lang)) return MOCK_FLAGGED_MESSAGES_ES;

  return MOCK_FLAGGED_MESSAGES_ES.map((msg) => {
    const override = PT_FLAGGED_OVERRIDES[msg.annotationId];
    if (!override) return msg;
    return { ...msg, content: override.content };
  });
}

export function getMockTopicRanking(lang: string): TopicItem[] {
  if (!isPortuguese(lang)) return MOCK_TOPIC_RANKING_ES;

  return MOCK_TOPIC_RANKING_ES.map((topic) => {
    const override = topic.topicId ? PT_TOPIC_OVERRIDES[topic.topicId] : undefined;
    if (!override) return topic;
    return { ...topic, disciplineName: override.disciplineName, topic: override.topic };
  });
}

export function getMockTopicDetail(lang: string): Record<string, TopicDetailUser[]> {
  // Topic detail users contain only names — no language-dependent text
  if (!isPortuguese(lang)) return MOCK_TOPIC_DETAIL_ES;
  return MOCK_TOPIC_DETAIL_ES;
}
